import React, { Component } from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { DayModifiers } from 'react-day-picker';

import BaseFilter from './BaseFilter';
import { H3 } from '../Typography';
import { DayPicker } from '../DayPicker';

import dateMax from 'date-fns/max';
import dateMin from 'date-fns/min';
import addMonths from 'date-fns/addMonths';
import { parseISOStrict, toISODatePartStr } from '../../helpers/date';
import endOfDay from 'date-fns/endOfDay';
import addDays from 'date-fns/addDays';
import startOfDay from 'date-fns/startOfDay';
import { getCurrentLanguage } from '../../i18n';
import formatISO from 'date-fns/formatISO';

interface DateRangeValues {
    from: string;
    to: string;
}

interface DateRangeFilterProps extends WithTranslation {
    name: string;
    label?: string;
    disabled?: boolean;
    values: DateRangeValues | undefined;
    onApply: (name: string, values: DateRangeValues) => void;
    onDelete: (name: string) => void;
    isRangeUnlimited?: boolean;
    outputDateFormat?: 'date' | 'iso';
    endInclusive?: boolean;
}

interface FilterSelectState {
    from: Date | undefined;
    to: Date | undefined;
    months: string[];
    weekdaysLong: string[];
    weekdaysShort: string[];
}

const getLimits = (from: Date | undefined, to: Date | undefined) => {
    const lowerLimit = new Date(2018, 0, 1);
    const higherLimit = new Date();
    if (from && to) {
        return {
            before: dateMax([addMonths(to, -3), lowerLimit]),
            after: dateMin([addMonths(from, 3), higherLimit]),
        };
    }

    return { before: lowerLimit, after: higherLimit };
};

const getMonths = (lng: string) => {
    const months: string[] = [];
    const monthFormatter = new Intl.DateTimeFormat(lng, { month: 'long' });
    for (let i = 0; i < 12; i++) {
        const month = new Date(2019, i);
        months.push(monthFormatter.format(month));
    }
    return months;
};
const getWeekdaysLong = (lng: string) => {
    const days: string[] = [];
    const dayFormatter = new Intl.DateTimeFormat(lng, { weekday: 'long' });
    // note 2017 started on a sunday, and we need an array starting on a sunday
    for (let i = 1; i < 8; i++) {
        const day = new Date(2017, 0, i);
        days.push(dayFormatter.format(day));
    }
    return days;
};

const getWeekdaysShort = (lng: string) => {
    const days: string[] = [];
    const dayFormatter = new Intl.DateTimeFormat(lng, { weekday: 'narrow' });
    // note 2017 started on a sunday, and we need an array starting on a sunday
    for (let i = 1; i < 8; i++) {
        const day = new Date(2017, 0, i);
        days.push(dayFormatter.format(day));
    }
    return days;
};

class DateRangeFilter extends Component<DateRangeFilterProps, FilterSelectState> {
    constructor(props: DateRangeFilterProps, context: any) {
        super(props, context);

        const lng = getCurrentLanguage(props.i18n);
        this.state = {
            from: props.values && parseISOStrict(props.values.from),
            to: props.values && parseISOStrict(props.values.to),
            months: getMonths(lng || ''),
            weekdaysLong: getWeekdaysLong(lng || ''),
            weekdaysShort: getWeekdaysShort(lng || ''),
        };
        this.handeDayClick = this.handeDayClick.bind(this);
    }

    componentDidUpdate(prevProps: Readonly<DateRangeFilterProps>, prevState: Readonly<FilterSelectState>, snapshot?: any): void {
        const nextFrom = this.props.values && parseISOStrict(this.props.values.from || '');
        if (nextFrom && nextFrom.getTime() !== (this.state.from ? this.state.from.getTime() : 0)) {
            this.setState({ from: nextFrom });
        }
        const nextTo = this.props.values && parseISOStrict(this.props.values.to || '');
        if (nextTo && nextTo.getTime() !== (this.state.to ? this.state.to.getTime() : 0)) {
            this.setState({ to: nextTo });
        }
    }

    handeDayClick(value: Date, modifiers: DayModifiers) {
        if (modifiers.disabled) {
            this.setState({
                from: undefined,
                to: undefined,
            });
            return;
        }

        // if from is set, but not to, or both from and to date are set and equal
        if (
            (this.state.from && !this.state.to) ||
            (this.state.from && this.state.to && this.state.from.valueOf() === this.state.to.valueOf())
        ) {
            const lowest = dateMin([this.state.from, value]);
            const highest = dateMax([this.state.from, value]);

            this.setState({
                from: lowest,
                to: highest,
            });
            return;
        }
        // if no value was set, or both the from and to date is set and they
        // (and implicitly they are not equal)
        if (!this.state.from || (this.state.from && this.state.to)) {
            this.setState({
                from: value,
                to: value,
            });
            return;
        }
    }

    getFormattedOutput() {
        const outputDateFormat = this.props.outputDateFormat ?? 'date';
        const endInclusive = this.props.endInclusive ?? true; // Keep old behaviour as default

        const formatOutputDate = (date: Date | undefined) => {
            if (!date) {
                return '';
            }
            if (outputDateFormat === 'iso') {
                return formatISO(date);
            }
            return toISODatePartStr(date);
        };

        const interval = { from: '', to: '' };
        if (this.state.from) {
            interval.from = formatOutputDate(startOfDay(this.state.from));
        }

        if (this.state.to) {
            if (endInclusive) {
                interval.to = formatOutputDate(endOfDay(this.state.to));
            } else {
                interval.to = formatOutputDate(addDays(startOfDay(this.state.to), 1));
            }
        }
        return interval;
    }

    render() {
        const { t, name, onApply, onDelete, disabled, values, isRangeUnlimited, label } = this.props;

        // range is limited to max span 3 months
        const disabledRange = !!isRangeUnlimited ? undefined : getLimits(this.state.from, this.state.to);
        const highlighted = this.state.from && this.state.to && { from: this.state.from, to: this.state.to };
        const dateLabel = label ?? (values && (values.from === values.to ? values.from : `${values.from} – ${values.to}`));

        return (
            <BaseFilter
                initialOpen={!values}
                name={name}
                label={dateLabel || t('components.filters.date_range.label_no_dates_set')}
                disabled={disabled}
                applyIsDisabled={!this.state.from || !this.state.to}
                onApply={() => {
                    onApply(name, this.getFormattedOutput());
                }}
                onDelete={() => {
                    onDelete(name);
                }}
            >
                <H3>{this.state.from ?
                    t('components.filters.date_range.title_to') : t('components.filters.date_range.title_from')}</H3>
                <DayPicker
                    onDayClick={this.handeDayClick}
                    selectedDays={this.state.from}
                    modifiers={{
                        disabled: disabledRange,
                        highlighted,
                    }}
                    firstDayOfWeek={1}
                    enableOutsideDaysClick={true}
                    fromMonth={new Date(2018, 0, 1)}
                    toMonth={new Date()}
                    initialMonth={this.state.from || new Date()}
                    months={
                        this.state.months as [
                            string,
                            string,
                            string,
                            string,
                            string,
                            string,
                            string,
                            string,
                            string,
                            string,
                            string,
                            string
                        ]
                    }
                    weekdaysLong={this.state.weekdaysLong as [string, string, string, string, string, string, string]}
                    weekdaysShort={this.state.weekdaysShort as [string, string, string, string, string, string, string]}
                />
            </BaseFilter>
        );
    }
}

export default withTranslation()<React.ComponentType<DateRangeFilterProps>>(DateRangeFilter);
